import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth, authFetch } from "@/lib/auth";

const FavCtx = createContext(null);

// Global favourites state — heart toggles on listing cards stay in sync
// across home, marketplace, detail and the /favourites page.
export function FavouritesProvider({ children }) {
  const { user } = useAuth();
  const [favs, setFavs] = useState(new Set());
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user) { setFavs(new Set()); setLoaded(false); return; }
    let dead = false;
    (async () => {
      try {
        const r = await authFetch("/api/favourites/ids");
        if (r.ok && !dead) { const j = await r.json(); setFavs(new Set(j.ids || [])); }
      } catch {} finally { if (!dead) setLoaded(true); }
    })();
    return () => { dead = true; };
  }, [user]);

  const toggleFav = useCallback(async (listingId) => {
    const was = favs.has(listingId);
    setFavs((s) => { const n = new Set(s); if (was) n.delete(listingId); else n.add(listingId); return n; });
    try {
      const r = await authFetch(`/api/favourites/${listingId}`, { method: "PUT" });
      if (!r.ok) throw new Error("toggle failed");
      const j = await r.json();
      setFavs((s) => { const n = new Set(s); if (j.favourited) n.add(listingId); else n.delete(listingId); return n; });
      return j.favourited;
    } catch {
      setFavs((s) => { const n = new Set(s); if (was) n.add(listingId); else n.delete(listingId); return n; });
      return was;
    }
  }, [favs]);

  return <FavCtx.Provider value={{ favs, loaded, toggleFav }}>{children}</FavCtx.Provider>;
}

export const useFavourites = () => useContext(FavCtx);
